<script setup>
  import { computed, ref, watch, onMounted, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const emit = defineEmits(['saved']);

  const model = defineModel();

  const newPage = {
    name: '',
    title: '',
    title_de: '',
    title_en: '',
    title_es: '',
    title_fr: '',
    title_it: '',
    title_pt: '',
    display_in_menu: false,
    display_saved_searches: false,
    home_page: false,
    direct_link: false,
    shortcut: false,
    highlight: false
  };

  const props = defineProps({
    languages: {
      type: Array,
      required: true
    },
    cohort_id: {
      type: Number,
      required: true
    }
  });

  const page = ref(newPage);
  const nameInput = ref(null);
  const errors = ref(null);

  const loadPage = async () => {
    try {
      const res = await fetch(`/admin/cohort_pages/${model.value}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      page.value = (await res.json()).page
    } catch (error) {
      console.error(`Error fetching page ${model.value}:`, error);
      page.value = { ...newPage };
    }
  };

  onMounted(async () => {
    if (model.value != 0) {
      await loadPage();
    }
    nextTick(() => {
      nameInput.value.focus();
    });
  });

  const cancel = () => {
    model.value = null;
  };

  const save = async () => {
    const url = creationMode.value ? '/admin/cohort_pages.json' : `/admin/cohort_pages/${model.value}.json`;
    const method = creationMode.value ? 'POST' : 'PUT';
    try {
      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ cohort_page: page.value, cohort_id: props.cohort_id })
      });
      if (res.ok) {
        emit('saved');
      } else {
        errors.value = (await res.json()).errors.join(', ');
      }
    } catch (error) {
      console.error(`Error ${creationMode.value ? 'creating' : 'updating'} page:`, error);
    }
  };

  const creationMode = computed(() => model.value === 0);

  watch(() => [page.value.display_in_menu, page.value.shortcut], ([displayInMenu, shortcut]) => {
    if (!displayInMenu) {
      page.value.shortcut = false;
    }
    if (!displayInMenu || shortcut) {
      page.value.highlight = false;
    }
  });
</script>

<template>
  <div class="row p-3">
    <div class="col-6">
      <h3 class="mb-3">{{ creationMode ? t('cohort_pages.new_page') : t('cohort_pages.edit_page', { title: page.title }) }}</h3>
      <div class="alert alert-danger" role="alert" v-if="errors">
        {{ errors }}
      </div>
      <div class="mb-3 row">
        <label for="name" class="col-4 col-form-label">{{ t('cohort_pages.name') }}</label>
        <div class="col-8">
          <div class="input-group">
            <span class="input-group-text">/page/</span>
            <input type="text" class="form-control" id="name" v-model="page.name" ref="nameInput">
          </div>
        </div>
      </div>
      <template v-for="lang in props.languages" :key="lang">
        <div class="mb-3 row">
          <label :for="`title_${lang}`" class="col-4 col-form-label">{{ t('cohort_pages.title') }} ({{ lang.toUpperCase() }})</label>
          <div class="col-8">
            <input type="text" class="form-control" :id="`title_${lang}`" v-model="page[`title_${lang}`]">
          </div>
        </div>
        <div class="mb-3 row">
          <label :for="`description_${lang}`" class="col-4 col-form-label">{{ t('cohort_pages.description') }} ({{ lang.toUpperCase() }})</label>
          <div class="col-8">
            <textarea class="form-control" :id="`description_${lang}`" v-model="page[`description_${lang}`]"></textarea>
          </div>
        </div>
      </template>
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="display_in_menu" v-model="page.display_in_menu">
        <label class="form-check-label" for="display_in_menu">{{ t('cohort_pages.display_in_menu') }}</label>
      </div>
      <div class="form-check ms-4">
        <input class="form-check-input" type="checkbox" value="" id="shortcut" v-model="page.shortcut" :disabled="!page.display_in_menu">
        <label class="form-check-label" for="shortcut">{{ t('cohort_pages.shortcut') }}</label>
      </div>
      <div class="form-check ms-4">
        <input class="form-check-input" type="checkbox" value="" id="highlight" v-model="page.highlight" :disabled="!page.display_in_menu || page.shortcut">
        <label class="form-check-label" for="highlight">{{ t('cohort_pages.highlight') }}</label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="direct_link" v-model="page.direct_link">
        <label class="form-check-label" for="direct_link">{{ t('cohort_pages.direct_link') }}</label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="display_saved_searches" v-model="page.display_saved_searches"  :disabled="page.direct_link">
        <label class="form-check-label" for="display_saved_searches">{{ t('cohort_pages.display_saved_searches') }}</label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="home_page" v-model="page.home_page" :disabled="page.direct_link">
        <label class="form-check-label" for="home_page">{{ t('cohort_pages.home_page') }}</label>
      </div>
      <div class="row">
        <div class="col d-flex justify-content-end">
          <button class="btn btn-primary me-2" @click="save">{{ creationMode ? t('cohort_pages.create') : t('cohort_pages.update') }}</button>
          <button class="btn btn-secondary" @click="cancel">{{ t('cohort_pages.cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
</style>

<i18n src="../locales/en.json" global></i18n>
<i18n src="../locales/fr.json" global></i18n>
<i18n src="../locales/es.json" global></i18n>
<i18n src="../locales/it.json" global></i18n>
<i18n src="../locales/pt.json" global></i18n>
<i18n src="../locales/de.json" global></i18n>
